import type { IncomingMessage, ServerResponse } from 'node:http';

import type { VWODecisionData } from '@aspendental/shared-components-web';
import { getFeatureFlags, parseFlagOverrides } from '@aspendental/shared-components-web';
import { PAGE_CACHE_CONTROL } from '@aspendental/web-shared-utils';
import type { Logger } from 'pino';

import { buildTargetingVariables } from './build-targeting-variables';
import { getVwoFmeAccountId, getVwoFmeSdkKey } from './get-vwo-fme-config';
import { omitUndefined } from './omit-undefined';
import { resolveVariationId } from './resolve-variation-id';
import { getOrCreateVisitorId } from './visitor-id';

export interface ResolvedFeatureFlag {
	isEnabled: boolean;
	variationId: string | null;
}

export interface VWOFlagDecision extends VWODecisionData {
	featureFlagKey: string;
}

export interface ResolveFeatureFlagsResult<K extends string> {
	flags: Record<K, ResolvedFeatureFlag>;
	decisions: VWOFlagDecision[];
}

/**
 * A response may be shared-cacheable, or it may depend on / set `vwo_fme_id` —
 * never both.
 *
 * VWO variations are resolved here, server-side, and baked into the HTML. These
 * apps sit behind CloudFront, which does not put cookies in the cache key,
 * ignores `Vary` from custom origins, and caches and replays `Set-Cookie`. A
 * `public`/`s-maxage` response carrying a resolved variation therefore serves
 * one visitor's variation to every visitor on that path for the whole TTL:
 * the experiment readout is diluted, bucketing consistency is broken, and
 * `VWOExperimentPush` attributes the cache-fill visitor's decision to whoever
 * received the replay.
 *
 * Pinning here rather than in each page's `getServerSideProps` is deliberate:
 * the response becomes non-shared-cacheable at the source of the variation, so
 * a future page cannot reintroduce the bug merely by calling
 * `resolveFeatureFlags` — in any order, which matters because `_app` tells
 * engineers to add that call to every page that mounts the default Layout.
 * `setCacheHeader` is set-if-unset, so no page-level change is needed either
 * way.
 *
 * Do NOT relax this to a shared-cacheable directive without first putting the
 * variation dimension into the CloudFront cache key. See `docs/vwo-cdn-caching.md`
 * in the `aspen-marketing` repo.
 */
function pinResponseAsUncacheable(res: ServerResponse): void {
	// Unconditional, deliberately. There is no directive that can legitimately be
	// set earlier in this request and should survive: the only other writer in
	// this path is getOrCreateVisitorId's `private, no-store`, and
	// PAGE_CACHE_CONTROL (`private, no-store, must-revalidate`) is a superset of
	// it. A set-if-unset guard here would instead yield to a shared-cacheable
	// directive from an earlier setCacheHeader call — which is precisely the bug
	// this function exists to prevent — and would make the ordering of two calls
	// inside a page's getServerSideProps silently load-bearing.
	res.setHeader('Cache-Control', PAGE_CACHE_CONTROL);
}

function emptyResult<K extends string>(flagKeys: readonly K[]): ResolveFeatureFlagsResult<K> {
	const flags = {} as Record<K, ResolvedFeatureFlag>;
	for (const key of flagKeys) {
		flags[key] = { isEnabled: false, variationId: null };
	}
	return { flags, decisions: [] };
}

/**
 * Resolves a list of VWO FME feature flags server-side. Use this for flags
 * that gate behavior outside of Contentful ExperimentContainer entries
 * (e.g., layout-level Header swap driven by `showNewHeader`).
 *
 * Returns:
 *  - `flags[key].isEnabled` — whether the flag is on for this visitor
 *  - `flags[key].variationId` — VWO experimentVariationId if present
 *  - `decisions` — array of decisions to feed into VWOExperimentPush
 */
export async function resolveFeatureFlags<K extends string>(
	flagKeys: readonly K[],
	config: BrandThemeConfig,
	req: IncomingMessage,
	res: ServerResponse,
	query: Record<string, string | string[] | undefined>,
	logger: Logger,
	facilityCode?: string
): Promise<ResolveFeatureFlagsResult<K>> {
	if (flagKeys.length === 0) return emptyResult(flagKeys);

	const accountId = getVwoFmeAccountId(config);
	const sdkKey = getVwoFmeSdkKey(config);
	if (!accountId || !sdkKey) {
		logger.info('[VWO FME] No VWO FME config for this brand, skipping feature flag resolution');
		return emptyResult(flagKeys);
	}

	const visitorId = getOrCreateVisitorId(req, res, logger);

	// From this point the response depends on `vwo_fme_id`, so it can no longer
	// be shared-cached — including when getFeatureFlags below throws.
	pinResponseAsUncacheable(res);

	const pLogger = {
		info: (msg: string, ctx?: Record<string, unknown>) => logger.info(ctx, `[VWO FME] ${msg}`),
		warn: (msg: string, ctx?: Record<string, unknown>) => logger.warn(ctx, `[VWO FME] ${msg}`),
		error: (msg: string, ctx?: Record<string, unknown>) => logger.error(ctx, `[VWO FME] ${msg}`),
	};
	const flagOverrides = parseFlagOverrides(query, pLogger);
	const customVariables = buildTargetingVariables(req, facilityCode);

	const result = emptyResult(flagKeys);

	try {
		const flags = await getFeatureFlags(
			[...flagKeys],
			{ visitorId, accountId, sdkKey, customVariables, flagOverrides },
			pLogger
		);

		for (const key of flagKeys) {
			const flag = flags[key];
			const variationId = resolveVariationId(flag);
			result.flags[key] = { isEnabled: !!flag?.isEnabled, variationId };
			if (flag?.experimentMeta) {
				result.decisions.push(omitUndefined({ featureFlagKey: key, ...flag.experimentMeta }));
			}
		}
	} catch (err) {
		logger.error(`[VWO FME] Error resolving feature flags: ${err}`);
	}

	return result;
}
